import React from "react";
import { useLocation, Link } from "react-router-dom";
import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import { MiniLeadForm } from "./components/MiniLeadForm";
import { useDocumentMeta } from "./hooks/useDocumentMeta";
import {
  PLATFORM_PAGES,
  CATEGORY_PAGES,
  GUIDE_PAGES,
  PLATFORM_TO_GUIDES,
  GUIDE_TO_PLATFORMS,
  findPageBySlug,
} from "./data/seoPages";

/**
 * SeoStubPage
 *
 * Renders a single placeholder route in one of three template families
 * (platform / category / guide). Every stub carries `robots: noindex,
 * nofollow` so it cannot be crawled or indexed until real content ships.
 *
 * The stub still renders a small amount of navigational content (headline,
 * short shell copy, and the required cross-links to /qc-consulting,
 * /for-platforms, and two related guides), so a human landing here from
 * a shared link isn't dropped into a blank page. Crawlers see the noindex
 * and stop, so there is no thin-content SEO risk.
 *
 * A dormant Service (platform/category) or Article (guide) JSON-LD is
 * still emitted so when the page ships real copy and `noindex` is
 * removed, the schema is already correct and Google can pick it up
 * immediately.
 *
 * When a page ships real copy, its route in App.js should be swapped out
 * for a dedicated component that removes the noindex and lands it in the
 * sitemap.
 */
const buildServiceSchema = (page, canonical) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: page.label,
  serviceType: "Quick commerce onboarding and growth",
  provider: {
    "@type": "Organization",
    name: "PickQuick",
    legalName: "Civicgrid Technologies Pvt. Ltd.",
    url: "https://letspickquick.com",
  },
  areaServed: { "@type": "Country", "name": "India" },
  url: canonical,
  description: page.description,
});

const buildArticleSchema = (page, canonical) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: page.label,
  description: page.description,
  mainEntityOfPage: canonical,
  author: {
    "@type": "Organization",
    name: "PickQuick",
    url: "https://letspickquick.com",
  },
  publisher: {
    "@type": "Organization",
    name: "PickQuick",
    logo: {
      "@type": "ImageObject",
      url: "https://letspickquick.com/pickquick-logo.png",
    },
  },
});

const SeoStubPage = ({ family }) => {
  const location = useLocation();
  // Derive the slug from the actual URL (leading slash stripped).
  // Works for both root-level slugs and nested /guides/xxx.
  const slug = location.pathname.replace(/^\//, "").replace(/\/$/, "");
  const page = findPageBySlug(slug);

  const canonical = `https://letspickquick.com/${slug}`;
  const title = page?.title || (page ? `${page.label} | PickQuick` : "PickQuick");
  const description =
    page?.description ||
    (page ? `${page.label} on quick commerce, by PickQuick.` : "PickQuick.");

  // Emit dormant schema now so it's live the moment noindex is removed.
  const dormantSchema = page
    ? family === "guide"
      ? buildArticleSchema(page, canonical)
      : buildServiceSchema(page, canonical)
    : null;

  useDocumentMeta({
    title,
    description,
    canonical,
    ogUrl: canonical,
    ogType: family === "guide" ? "article" : "website",
    // Critical: stubs must not be indexed. `nofollow` is intentional to
    // prevent link-equity leakage during the placeholder phase.
    robots: "noindex, nofollow",
    schema: dormantSchema,
  });

  // Related links per family
  let relatedGuides = [];
  let relatedPlatforms = [];
  if (family === "platform") {
    relatedGuides = (PLATFORM_TO_GUIDES[slug] || []).map((s) => findPageBySlug(s)).filter(Boolean);
  } else if (family === "guide") {
    relatedPlatforms = (GUIDE_TO_PLATFORMS[slug] || []).map((s) => findPageBySlug(s)).filter(Boolean);
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#000000", color: "#FFFFFF" }}>
      <SiteHeader />
      <main className="flex-1 py-24" data-testid={`seo-stub-${family}`}>
        <div className="container" style={{ maxWidth: "820px" }}>
          <p
            className="text-xs font-semibold uppercase mb-4"
            style={{ color: "#F1592B", letterSpacing: "0.18em" }}
          >
            {family === "platform" ? "Platform page" : family === "guide" ? "Guide" : "Category"}
          </p>
          <h1
            className="text-white font-bold"
            style={{ fontSize: "clamp(30px, 4vw, 48px)", lineHeight: 1.1, letterSpacing: "-0.02em" }}
          >
            {page?.label || "PickQuick"}
          </h1>
          <p className="mt-6" style={{ color: "#B8B8B8", fontSize: "16px", lineHeight: 1.65 }}>
            Our operators can walk you through this on a live call, or get a free QC audit below.
          </p>

          {/* Embedded QC audit lead form. Same MiniLeadForm as the homepage
              popup, so submissions land in the same lead pipeline. The
              `pagePath` prop puts a "Page:" line in the WhatsApp draft so
              leads from different guides are distinguishable. */}
          <div className="mt-8" data-testid={`seo-stub-audit-${slug.replace(/\//g, "-")}`}>
            <MiniLeadForm
              source={`stub-${slug.replace(/\//g, "-")}`}
              variant="popup"
              pagePath={`/${slug}`}
              headline={`Free QC audit: see where your brand is leaking revenue`}
              subhead="One platform, one category, no cost. We open WhatsApp with your details pre-filled."
            />
          </div>

          {/* Secondary navigation: keep the three contextual links, but
              demote them below the form now that the audit form is the
              primary CTA on the page. */}
          <div className="mt-12">
            <p style={{ color: "#8A8A8A", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700 }}>
              Related on PickQuick
            </p>
            <ul className="mt-3 space-y-2" style={{ color: "#B8B8B8", fontSize: "14px" }}>
              <li>
                <Link to="/qc-consulting" style={{ color: "#F1592B", textDecoration: "underline" }}>
                  QC Consulting, the demand-decision engine
                </Link>
              </li>
              <li>
                <Link to="/for-platforms" style={{ color: "#F1592B", textDecoration: "underline" }}>
                  PickQuick for Platforms
                </Link>
              </li>
              {relatedGuides.map((g) => (
                <li key={g.slug}>
                  <Link to={`/${g.slug}`} style={{ color: "#F1592B", textDecoration: "underline" }}>
                    {g.label}
                  </Link>
                </li>
              ))}
              {relatedPlatforms.map((p) => (
                <li key={p.slug}>
                  <Link to={`/${p.slug}`} style={{ color: "#F1592B", textDecoration: "underline" }}>
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

// Export named variants so App.js can wire them cleanly per family.
export const PlatformStub = () => <SeoStubPage family="platform" />;
export const CategoryStub = () => <SeoStubPage family="category" />;
export const GuideStub    = () => <SeoStubPage family="guide" />;

export { PLATFORM_PAGES, CATEGORY_PAGES, GUIDE_PAGES };
export default SeoStubPage;
